import { useState, useEffect, useMemo } from "react";
import ModalForm from "../../../../../components/ModalForm";
import AlertMessage, { AlertLevel } from "../../../../../components/AlertMessage";
import {
	useGetCleverSettingsInfoQuery,
	DistrictCleverInfoResult,
	useUpdateCleverSettingsInfoMutation,
} from "../../../districtsApiSlice";
import CleverSettingsForm from "./CleverSettingsForm";

export interface DistrictCleverInfoSectionProps {
	districtId: number;
	isOutsideSiteFormOpen: boolean;
	setIsOutsideSiteFormOpen: (value: boolean) => void;
	onChange: (patch: Partial<{ cleverId: string }>) => void;
}

export default function DistrictCleverSettings({
	districtId,
	isOutsideSiteFormOpen,
	setIsOutsideSiteFormOpen,
	onChange,
}: DistrictCleverInfoSectionProps) {
	const [cleverData, setCleverData] = useState<DistrictCleverInfoResult>();
	const [originalCleverData, setOriginalCleverData] = useState<DistrictCleverInfoResult>();
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState("");

	const { data: cleverSettingsData, isLoading } = useGetCleverSettingsInfoQuery(districtId);

	const [updateCleverSettings] = useUpdateCleverSettingsInfoMutation();

	// Reset local state to server data when modal opens and store original data
	useEffect(() => {
		if (isOutsideSiteFormOpen && cleverSettingsData) {
			setCleverData(cleverSettingsData);
			setOriginalCleverData(cleverSettingsData);
		}
	}, [isOutsideSiteFormOpen, cleverSettingsData]);

	const hasChanges = useMemo(() => {
		const currentData = cleverData;
		const originalData = originalCleverData;
		return JSON.stringify(currentData) !== JSON.stringify(originalData);
	}, [cleverData, originalCleverData]);

	const handleSubmit = () => {
		setIsSaving(true);
		if (cleverData) {
			updateCleverSettings({
				districtId: districtId,
				cleverId: cleverData.cleverId,
			})
				.unwrap()
				.then(() => {
					onChange({ cleverId: cleverData.cleverId });
					onModalClosed();
				})
				.catch((error) => {
					setSaveError(error.data);
					setIsSaving(false);
				});
		}
	};

	const onModalClosed = () => {
		setIsOutsideSiteFormOpen(false);
		setIsSaving(false);
		setSaveError("");
	};

	return (
		<ModalForm
			open={isOutsideSiteFormOpen}
			title={"Clever Settings"}
			form={
				<>
					<AlertMessage
						className="my-2"
						type={AlertLevel.Error}
						message={saveError}
						showIcon={true}
						onClear={() => setSaveError("")}
					/>
					<CleverSettingsForm
						data={cleverData}
						setData={setCleverData}
						isLoading={isLoading}
					/>
				</>
			}
			confirmLabel="Save"
			disableConfirm={!hasChanges || isSaving || isLoading}
			onConfirm={handleSubmit}
			onCancel={onModalClosed}
		/>
	);
}
